/**
 * Advanced Keyboard Shortcuts Hook - WMS DAOUXUN
 * 
 * Complete keyboard shortcut system for fast navigation
 * and contextual actions in the WMS application.
 * 
 * Features:
 * - Fast navigation between modules
 * - Context-aware page actions
 * - Global system shortcuts
 * - Conflict and priority management
 * - Multi-platform support (Mac/PC)
 * - Contextual help (?)
 */
import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Shortcut configuration by context
const SHORTCUT_CONTEXTS = {
  GLOBAL: 'global',
  DASHBOARD: 'dashboard',
  INVENTORY: 'inventory_items',
  ORDERS: 'orders',
  ANALYTICS: 'analytics',
  ADMIN: 'admin'
};

// Global shortcut definitions
const GLOBAL_SHORTCUTS = {
  // Main navigation
  'g+d': { action: 'navigate', target: '/dashboard', description: 'Go to Dashboard' },
  'g+i': { action: 'navigate', target: '/admin/inventory', description: 'Go to Inventory' },
  'g+o': { action: 'navigate', target: '/admin/orders', description: 'Go to Orders' },
  'g+w': { action: 'navigate', target: '/admin/warehouses', description: 'Go to Warehouses' },
  'g+a': { action: 'navigate', target: '/admin/analytics', description: 'Go to Analytics' },
  'g+m': { action: 'navigate', target: '/admin/monitoring', description: 'Go to Monitoring' },
  'g+s': { action: 'navigate', target: '/admin/shipping', description: 'Go to Shipping' },
  'g+r': { action: 'navigate', target: '/admin/reports', description: 'Go to Reports' },
  
  // System actions
  'ctrl+k': { action: 'callback', callback: 'onOpenSearch', description: 'Global search' },
  'ctrl+shift+t': { action: 'callback', callback: 'onToggleTheme', description: 'Toggle theme' },
  'ctrl+shift+n': { action: 'callback', callback: 'onOpenNotifications', description: 'Open notifications' },
  'ctrl+shift+p': { action: 'callback', callback: 'onOpenPreferences', description: 'Open preferences' },
  
  // Help and navigation
  '?': { action: 'callback', callback: 'onShowHelp', description: 'Show help' },
  'escape': { action: 'callback', callback: 'onCloseModal', description: 'Close modal/overlay' },
  
  // Quick actions
  'ctrl+n': { action: 'callback', callback: 'onNewItem', description: 'New (contextual)' },
  'ctrl+s': { action: 'callback', callback: 'onSave', description: 'Save (contextual)' },
  'ctrl+f': { action: 'callback', callback: 'onFind', description: 'Search in page' },
  'f5': { action: 'callback', callback: 'onRefresh', description: 'Refresh data' }
};

// Context-specific shortcuts
const CONTEXT_SHORTCUTS = {
  [SHORTCUT_CONTEXTS.INVENTORY]: {
    'a': { action: 'callback', callback: 'onAddProduct', description: 'Add a product' },
    'e': { action: 'callback', callback: 'onExport', description: 'Export inventory' },
    'r': { action: 'callback', callback: 'onReceive', description: 'Stock receiving' },
    't': { action: 'callback', callback: 'onTransfer', description: 'Stock transfer' }
  },
  [SHORTCUT_CONTEXTS.ORDERS]: {
    'n': { action: 'callback', callback: 'onNewOrder', description: 'New order' },
    'p': { action: 'callback', callback: 'onProcess', description: 'Process order' },
    'e': { action: 'callback', callback: 'onExport', description: 'Export orders' },
    's': { action: 'callback', callback: 'onShip', description: 'Ship order' }
  },
  [SHORTCUT_CONTEXTS.ANALYTICS]: {
    '1': { action: 'callback', callback: 'onShowKPIs', description: 'Show KPIs' },
    '2': { action: 'callback', callback: 'onShowCharts', description: 'Show charts' },
    '3': { action: 'callback', callback: 'onShowReports', description: 'Show reports' },
    'e': { action: 'callback', callback: 'onExport', description: 'Export data' }
  }
};

const useAdvancedShortcuts = (callbacks = {}, _context = SHORTCUT_CONTEXTS.GLOBAL) => {
  const { _t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isHelpVisible, setIsHelpVisible] = useState(false);
  const [keySequence, setKeySequence] = useState('');
  const [sequenceTimeout, setSequenceTimeout] = useState(null);

  // Platform detection
  const isMac = typeof window !== 'undefined' && 
    /Mac|iPod|iPhone|iPad/.test(window.navigator.platform);

  // Check if active element is an input field
  const isInputActive = useCallback(() => {
    const activeElement = document.activeElement;
    const inputTypes = ['input', 'textarea', 'select'];
    const isContentEditable = activeElement?.contentEditable === 'true';
    
    return inputTypes.includes(activeElement?.tagName?.toLowerCase()) || 
           isContentEditable ||
           activeElement?.getAttribute('role') === 'textbox';
  }, []);

  // Determine current context based on URL
  const getCurrentContext = useCallback(() => {
    const path = location.pathname;
    if (path.includes('/inventory')) return SHORTCUT_CONTEXTS.INVENTORY;
    if (path.includes('/orders')) return SHORTCUT_CONTEXTS.ORDERS;
    if (path.includes('/analytics')) return SHORTCUT_CONTEXTS.ANALYTICS;
    if (path.includes('/admin')) return SHORTCUT_CONTEXTS.ADMIN;
    if (path.includes('/dashboard')) return SHORTCUT_CONTEXTS.DASHBOARD;
    return SHORTCUT_CONTEXTS.GLOBAL;
  }, [location.pathname]);

  // Get available shortcuts for current context
  const getAvailableShortcuts = useCallback(() => {
    const currentContext = getCurrentContext();
    const contextShortcuts = CONTEXT_SHORTCUTS[currentContext] || {};
    return { ...GLOBAL_SHORTCUTS, ...contextShortcuts };
  }, [getCurrentContext]);

  // Execute a shortcut action
  const executeShortcut = useCallback((shortcut) => {
    if (shortcut.action === 'navigate') {
      navigate(shortcut.target);
    } else if (shortcut.action === 'callback' && shortcut.callback) {
      const callback = callbacks[shortcut.callback];
      if (typeof callback === 'function') {
        callback();
      }
    }
  }, [navigate, callbacks]);

  // Handle key sequences (like g+d)
  const handleKeySequence = useCallback((key) => {
    // Clear previous timeout
    if (sequenceTimeout) {
      clearTimeout(sequenceTimeout);
    }

    const newSequence = keySequence + key;
    setKeySequence(newSequence);

    // Check if sequence matches a shortcut
    const shortcuts = getAvailableShortcuts();
    const matchingShortcut = shortcuts[newSequence];

    if (matchingShortcut) {
      // Execute shortcut and reset
      executeShortcut(matchingShortcut);
      setKeySequence('');
    } else {
      // Check if any shortcuts start with this sequence
      const hasPartialMatch = Object.keys(shortcuts).some(
        shortcutKey => shortcutKey.startsWith(newSequence)
      );

      if (hasPartialMatch) {
        // Wait for rest of sequence
        const timeout = setTimeout(() => {
          setKeySequence('');
        }, 1000); // 1 second to complete the sequence
        setSequenceTimeout(timeout);
      } else {
        // No match, reset
        setKeySequence('');
      }
    }
  }, [keySequence, sequenceTimeout, getAvailableShortcuts, executeShortcut]);

  // Main shortcut handler
  const handleKeyDown = useCallback((event) => {
    const { key, ctrlKey, metaKey, altKey, shiftKey } = event;
    
    // Don't process shortcuts if in an input field
    // Exception: Escape and F5 always work
    if (isInputActive() && key !== 'Escape' && key !== 'F5') {
      return;
    }

    // Use Cmd on Mac, Ctrl on other platforms
    const isModifierPressed = isMac ? metaKey : ctrlKey;
    
    // Build the shortcut key
    let shortcutKey = '';
    
    if (isModifierPressed && shiftKey) {
      shortcutKey = `ctrl+shift+${key.toLowerCase()}`;
    } else if (isModifierPressed) {
      shortcutKey = `ctrl+${key.toLowerCase()}`;
    } else if (altKey) {
      shortcutKey = `alt+${key.toLowerCase()}`;
    } else {
      shortcutKey = key.toLowerCase();
    }

    // Check direct shortcuts
    const shortcuts = getAvailableShortcuts();
    const directShortcut = shortcuts[shortcutKey];

    if (directShortcut) {
      event.preventDefault();
      event.stopPropagation();
      executeShortcut(directShortcut);
      return;
    }

    // Handle key sequences (without modifiers)
    if (!isModifierPressed && !altKey && !shiftKey && key.length === 1) {
      event.preventDefault();
      handleKeySequence(key.toLowerCase());
    }

    // Special shortcut for help
    if (key === '?' && !isModifierPressed) {
      event.preventDefault();
      setIsHelpVisible(prev => !prev);
    }
  }, [isInputActive, isMac, getAvailableShortcuts, executeShortcut, handleKeySequence]);

  // Show shortcut help
  const getShortcutHelp = useCallback(() => {
    const shortcuts = getAvailableShortcuts();
    const currentContext = getCurrentContext();
    
    return {
      context: currentContext,
      shortcuts: Object.entries(shortcuts).map(([key, shortcut]) => ({
        key: key.replace('ctrl', isMac ? 'Cmd' : 'Ctrl'),
        description: shortcut.description
      }))
    };
  }, [getAvailableShortcuts, getCurrentContext, isMac]);

  // Set up event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (sequenceTimeout) {
        clearTimeout(sequenceTimeout);
      }
    };
  }, [handleKeyDown, sequenceTimeout]);

  // Clear sequence when context changes
  useEffect(() => {
    setKeySequence('');
    if (sequenceTimeout) {
      clearTimeout(sequenceTimeout);
    }
  }, [location.pathname, sequenceTimeout]);

  return {
    isHelpVisible,
    setIsHelpVisible,
    getShortcutHelp,
    currentContext: getCurrentContext(),
    keySequence,
    availableShortcuts: getAvailableShortcuts()
  };
};

export default useAdvancedShortcuts;
export { SHORTCUT_CONTEXTS };

